import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { Headphones, Mic, Play, Crown, Heart, Scroll, Flame, Layers, ArrowRight, Volume2, Clock, Sparkles, Bell, Music, Quote, Mountain, Lock, Radio, Users, ChevronRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-4198" data-mp="3mp8gnivbk0p7">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro,
  align = 'center'
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
  align?: 'center' | 'left';
}) => <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`} data-id="element-4199" data-mp="2yz5pr63550s6">
    <Eyebrow data-id="element-4200" data-mp="yqeb78lgduv5">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-4201" data-mp="ypcna3jc9dw0">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-4202" data-mp="pxj223sv7f03">{highlight}</span>
        </>}
    </h2>
    <div className={`w-16 h-px bg-amber-400/30 mb-6 ${align === 'center' ? 'mx-auto' : ''}`} data-id="element-4203" data-mp="kzrbpe7vc0y0" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-4204" data-mp="g17mn4xza91f">
        {intro}
      </p>}
  </div>;
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-amber-400/40 pointer-events-none" data-id="element-4205" data-mp="6yyicwtazbwh" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-amber-400/40 pointer-events-none" data-id="element-4206" data-mp="21f0zo1b6j0b" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-amber-400/40 pointer-events-none" data-id="element-4207" data-mp="21czdb6lqfera" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-amber-400/40 pointer-events-none" data-id="element-4208" data-mp="1wcbngo98p3yn" />
  </>;
// HERO
const Hero = () => <section className="relative w-full min-h-[100vh] bg-black flex items-center justify-center overflow-hidden pt-20" data-id="element-4209" data-mp="2k9zy1i63jwod">
    <div className="absolute inset-0" data-id="element-4210" data-mp="26jmppepamfyi">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.18),_transparent_55%)] pointer-events-none" data-id="element-4211" data-mp="2bhfrlvjkcnsz" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(139,0,0,0.15),_transparent_60%)] pointer-events-none" data-id="element-4212" data-mp="2z8201nxhca3i" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" data-id="element-4213" data-mp="336huudzsymrh" />
    </div>

    <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 0.15
  }} transition={{
    duration: 2
  }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-4214" data-mp="2paxade4fdwhy">
      <div className="flex items-end gap-1 h-40" data-id="element-4215" data-mp="1itsa4pdladak">
        {Array.from({
        length: 32
      }).map((_, i) => <motion.div key={i} animate={{
        scaleY: [0.3, 1, 0.5, 0.9, 0.3]
      }} transition={{
        duration: 3 + i % 5 * 0.4,
        repeat: Infinity,
        repeatType: 'mirror',
        delay: i * 0.05
      }} className="w-px bg-amber-400 origin-bottom" style={{
        height: `${20 + i % 6 * 12}px`
      }} data-id="element-4216" data-mp="1b7ok7fp6xdxd" data-mpi={i} />)}
      </div>
    </motion.div>

    <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-4217" data-mp="nl867webtgft">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7
    }} className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/30 mb-8 bg-black/40 backdrop-blur-sm" data-id="element-4218" data-mp="11c3kxpbiarrb">
        <Headphones className="w-3.5 h-3.5 text-amber-400" data-id="element-4219" data-mp="3tlboe44d18v" />
        <span className="text-amber-400 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-4220" data-mp="hjpt0hafxdqa">
          A Prestige Audio Franchise
        </span>
      </motion.div>

      <motion.h1 initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      delay: 0.15
    }} className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white leading-[0.98] tracking-tight mb-4" data-id="element-4221" data-mp="rg16vbpoibmu">
        House of <span className="text-amber-400" data-id="element-4222" data-mp="1a72gqusc7rl6">Lions</span>
      </motion.h1>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 0.35
    }} className="font-serif italic text-lg sm:text-xl md:text-2xl text-white/70 mb-7 tracking-wide" data-id="element-4223" data-mp="1a7u0978ntyv3">
        Voices of the Sikh Kingdoms
      </motion.p>

      <motion.div initial={{
      opacity: 0,
      scaleX: 0
    }} animate={{
      opacity: 1,
      scaleX: 1
    }} transition={{
      duration: 0.9,
      delay: 0.45
    }} className="w-32 h-px bg-amber-400/50 mx-auto mb-9 origin-center" data-id="element-4224" data-mp="1y2nkxu3qiek0" />

      <motion.p initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.9,
      delay: 0.55
    }} className="text-base sm:text-lg md:text-xl text-white/65 font-light leading-relaxed max-w-3xl mx-auto mb-12" data-id="element-4225" data-mp="1kck8tvykk18a">
        A living world of power, love, discipline, fracture, pluralism,
        inheritance, and loss — experienced not as a lesson from the past, but
        as a court still breathing.
      </motion.p>

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.75
    }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-4226" data-mp="rlsc8xdldakf">
        <a href="#episode-one" className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.35)]" data-id="element-4227" data-mp="k2115rznm5g2">
          <Play className="w-4 h-4 fill-black" data-id="element-4228" data-mp="f3zktetm4k8a" />
          Listen to Episode One
        </a>
        <a href="#season-one" className="group inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-4229" data-mp="1d44jhgsb0uo">
          Explore Season One
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-4230" data-mp="3shks2kcurbry" />
        </a>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 1
    }} className="mt-14 flex items-center justify-center gap-6 text-[10px] tracking-[0.3em] uppercase text-white/35" data-id="element-4231" data-mp="38x2mrql7m6lt">
        <span data-id="element-4232" data-mp="3rvopf9wdk5ru">8 Episodes</span>
        <span className="text-amber-400/30" data-id="element-4233" data-mp="3ruwr3rbe7txv">·</span>
        <span data-id="element-4234" data-mp="3rtls04sw7ad3">Season One</span>
        <span className="text-amber-400/30" data-id="element-4235" data-mp="3mx51j3y80jwu">·</span>
        <span data-id="element-4236" data-mp="3mwbr9t01zc9y">Now Recording</span>
      </motion.div>
    </div>
  </section>;
// CENTRAL QUESTION
const CentralQuestion = () => <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-4237" data-mp="h0slhmxllsi">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.04),_transparent_70%)] pointer-events-none" data-id="element-4238" data-mp="j3pmlmi4f6q" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-4239" data-mp="afq9p870727p">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8
    }} className="max-w-4xl mx-auto text-center" data-id="element-4240" data-mp="addkqb2tvrl5">
        <Eyebrow data-id="element-4241" data-mp="1uufn3wjibmfr">The Question That Bites</Eyebrow>
        <Quote className="w-8 h-8 text-amber-400/40 mx-auto mt-7 mb-7" strokeWidth={1.25} data-id="element-4242" data-mp="1ut4k3yxvyipp" />
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white leading-[1.15] tracking-tight mb-10" data-id="element-4243" data-mp="y1tvp6b0mlzn">
          What does it cost to build and hold a kingdom{' '}
          <span className="text-amber-400 italic" data-id="element-4244" data-mp="1731tzodj7mtq">worthy of memory</span> in a
          violent world?
        </h2>
        <div className="w-16 h-px bg-amber-400/40 mx-auto mb-8" data-id="element-4245" data-mp="1bx62wd6cy4xg" />
        <p className="text-base sm:text-lg text-white/55 font-light leading-relaxed italic max-w-2xl mx-auto" data-id="element-4246" data-mp="fcdwcmb2gzkt">
          And what kinds of people make an empire possible — what does history
          erase about them?
        </p>
      </motion.div>
    </div>
  </section>;
// THE WORLD
const TheWorld = () => {
  const pillars = [{
    icon: Crown,
    title: 'Court',
    body: 'Beauty, wit, etiquette, politics, ceremony, intimacy. The Darbar as a stage of human pressure.'
  }, {
    icon: Mountain,
    title: 'Frontier',
    body: 'Danger, endurance, cold steel, horses, dust, waiting, fear. Where empire is paid for in blood.'
  }, {
    icon: Flame,
    title: 'Faith & Discipline',
    body: 'Conviction, conscience, internal judgment, moral pressure. The unbending eye on power.'
  }, {
    icon: Layers,
    title: 'Pluralism & Statecraft',
    body: 'Diplomacy, coexistence, the burden of holding difference together without breaking it.'
  }, {
    icon: Scroll,
    title: 'Inheritance',
    body: 'Heirs, widows, memory, exile. What remains after the builders are gone.'
  }];
  return <section className="relative py-24 md:py-32 bg-black" data-id="element-4247" data-mp="dbdnz6wcbldy">
      <div className="container mx-auto px-4" data-id="element-4248" data-mp="d9su7jhuo6h5">
        <SectionHeading eyebrow="The World" title="Five Pressures That" highlight="Make the Kingdom" intro="Every episode lives inside the collision of these forces. The court is beautiful because it is difficult to hold." data-id="element-4249" data-mp="9b3j5xkab10o" />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto" data-id="element-4250" data-mp="3qifd4fk6ge74">
          {pillars.map((p, i) => <motion.div key={p.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className="relative border border-amber-400/15 hover:border-amber-400/45 transition-colors bg-gradient-to-b from-amber-950/10 to-transparent p-5" data-id="element-4251" data-mp="3mi5bff3f49y2" data-mpi={i}>
              <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-4252" data-mp="2xp2a029px4ul" />
              <div className="w-9 h-9 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-4" data-id="element-4253" data-mp="2jqay8mibvv9m">
                <p.icon className="w-4 h-4 text-amber-400" data-id="element-4254" data-mp="3ccvlbarrtl7d" />
              </div>
              <h3 className="font-serif text-lg text-white mb-2 leading-tight" data-id="element-4255" data-mp="3cezy6i7ndujs">
                {p.title}
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-4256" data-mp="32nc22405bpbz">
                {p.body}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// SEASON ONE
const SeasonOne = () => {
  const episodes = [{
    n: '01',
    title: 'A Fractured Punjab',
    body: 'The need for order is introduced as dangerous, necessary, and incomplete.',
    featured: true
  }, {
    n: '02',
    title: 'The Boy Who Survived',
    body: 'Ranjit Singh enters not as legend but as a marked young force of perception and will.'
  }, {
    n: '03',
    title: 'Lahore Taken',
    body: 'The city becomes the emotional and political heart of the story.'
  }, {
    n: '04',
    title: 'A Court of Many Worlds',
    body: 'Beauty, diplomacy, intimacy, and contradiction define the Darbar.'
  }, {
    n: '05',
    title: 'The Frontier Demands Blood',
    body: 'Nalwa and the edge of empire force the audience to confront cost.'
  }, {
    n: '06',
    title: 'Love, Reputation, and the Human Court',
    body: 'Moran shifts the center from public legend to interior life.'
  }, {
    n: '07',
    title: 'The Weight of Holding It Together',
    body: 'Moral and political tensions tighten around the builders.'
  }, {
    n: '08',
    title: 'What Remains, What Breaks',
    body: 'The season lands with beauty, power, and unresolved fragility.'
  }];
  return <section id="season-one" className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-4257" data-mp="1mt1938v2o0c1">
      <div className="container mx-auto px-4" data-id="element-4258" data-mp="1rtp0vwr4kl7b">
        <SectionHeading eyebrow="Season One" title="The Making of" highlight="Lahore" intro="A fractured land does not become a kingdom by force alone. It becomes one through scarred vision, dangerous loyalty, disciplined belief, intimate contradiction, and people willing to carry more than history remembers." data-id="element-4259" data-mp="1rquhm8jul6fh" />
        <div className="mt-16 max-w-5xl mx-auto" data-id="element-4260" data-mp="191e3ujj5jhh4">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="border-l-2 border-amber-400/50 pl-6 sm:pl-8 mb-12 max-w-3xl mx-auto" data-id="element-4261" data-mp="1dsmbba0x8srn">
            <p className="font-serif text-lg sm:text-xl text-white/80 leading-relaxed italic" data-id="element-4262" data-mp="2n20o0mr59sll">
              &ldquo;The kingdom begins where fracture becomes
              intolerable.&rdquo;
            </p>
            <p className="text-amber-400/60 text-[10px] tracking-[0.3em] uppercase mt-3" data-id="element-4263" data-mp="2wtn8zatab61j">
              — Episode One, closing line
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4264" data-mp="36of3g5rklcs3">
            {episodes.map((ep, i) => <motion.div key={ep.n} initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.04
          }} className={`relative border bg-gradient-to-b from-amber-950/8 to-transparent p-5 transition-colors ${ep.featured ? 'border-amber-400/50 hover:border-amber-400' : 'border-amber-400/15 hover:border-amber-400/35'}`} data-id="element-4265" data-mp="31q573ru4064w" data-mpi={i}>
                <span className={`absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent ${ep.featured ? 'via-amber-400' : 'via-amber-400/40'} to-transparent`} data-id="element-4266" data-mp="3j7uabvt11s75" />
                <div className="flex items-baseline gap-4 mb-2" data-id="element-4267" data-mp="398v38hnwu4m9">
                  <span className="font-mono text-amber-400/60 text-[11px] tracking-[0.2em] shrink-0" data-id="element-4268" data-mp="3eaajzxj21oyu">
                    EP {ep.n}
                  </span>
                  {ep.featured && <span className="text-[9px] tracking-[0.25em] uppercase text-amber-400 border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5" data-id="element-4269" data-mp="34c4lzr0dd2z9">
                      Featured
                    </span>}
                </div>
                <h4 className="font-serif text-lg text-white leading-tight mb-2" data-id="element-4270" data-mp="12hdr6t6xcecn">
                  {ep.title}
                </h4>
                <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-4271" data-mp="26h2901efbm08">
                  {ep.body}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
};
// ENSEMBLE
const Ensemble = () => {
  const cast = [{
    name: 'Maharaja Ranjit Singh',
    role: 'The Gravitational Center',
    desire: 'To build something larger than faction.',
    wound: 'A body marked early by illness and perception.',
    contradiction: 'Tenderness and ruthlessness.',
    motif: 'Restrained strings, low pulse — scarred authority, never triumph.'
  }, {
    name: 'Hari Singh Nalwa',
    role: 'Frontier Blade',
    desire: 'To secure the edge.',
    wound: 'A life defined by danger and action.',
    contradiction: 'Heroic necessity versus moral cost.',
    motif: 'Hard percussion, horse rhythm — long pauses that suggest the cost of force.'
  }, {
    name: 'Moran Sarkar',
    role: 'The Human Interior',
    desire: 'To be seen beyond reduction.',
    wound: 'The politics of intimacy and reputation.',
    contradiction: 'Softness in a hard court, wit in a punitive world.',
    motif: 'Lyrical fragments, intimate room tone — wit and ache, not softness alone.'
  }, {
    name: 'Fakir Azizuddin',
    role: 'Pluralism & Statecraft',
    desire: 'To hold the court together through intelligence and diplomacy.',
    wound: 'Speaking across difference in a world of suspicion.',
    contradiction: 'Gentle manner, razor political mind.',
    motif: 'Elegant tonal clarity, diplomatic steadiness, linguistic precision.'
  }, {
    name: 'Akali Phula Singh',
    role: 'Moral Force',
    desire: 'To keep moral force above political vanity.',
    wound: 'Unbending conscience in an imperfect world.',
    contradiction: 'Spiritual legitimacy and disruptive ferocity.',
    motif: 'Austerity, sharpened breath, conviction carried through stillness.'
  }, {
    name: 'Maharani Jind Kaur',
    role: 'Inheritance Under Pressure',
    desire: 'To preserve inheritance and agency.',
    wound: 'Entering power through fracture, not stability.',
    contradiction: 'Maternal tenderness and political steel.',
    motif: 'Tensioned regal motif — inheritance bending but not breaking.'
  }, {
    name: 'Duleep Singh',
    role: 'The Shadow of What Can Be Lost',
    desire: 'To belong to a story already breaking before he can hold it.',
    wound: 'Inheritance through removal and exile.',
    contradiction: 'Symbolic value versus personal dispossession.',
    motif: 'Motifs of distance and displacement — a kingdom no longer held.'
  }];
  return <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-4272" data-mp="5tifoxs56ctl">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-4273" data-mp="2hgucs5wdkkyp" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-4274" data-mp="2m8m2ke7pimns">
        <SectionHeading eyebrow="The Ensemble" title="Seven People Who" highlight="Carry the Court" intro="Each lives at the collision of a desire, a wound, and a contradiction. Each has a sonic identity you will recognize within seconds of an episode beginning." data-id="element-4275" data-mp="27iy37ij35y2p" />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-id="element-4276" data-mp="356ydo5i6i6m0">
          {cast.map((c, i) => <motion.div key={c.name} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="relative border border-amber-400/15 hover:border-amber-400/40 transition-colors bg-gradient-to-b from-amber-950/10 to-black p-6" data-id="element-4277" data-mp="2r642xg7nrtk6" data-mpi={i}>
              <CornerOrnaments data-id="element-4278" data-mp="c3e8di3k29w5" />
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-4279" data-mp="c6a89byyr8n8" />
              <p className="text-amber-400 text-[9px] tracking-[0.3em] uppercase font-bold mb-3" data-id="element-4280" data-mp="2dume3kjpzx2">
                {c.role}
              </p>
              <h3 className="font-serif text-xl text-white leading-tight mb-5" data-id="element-4281" data-mp="76nv12j4xsef">
                {c.name}
              </h3>
              <dl className="space-y-3 text-sm" data-id="element-4282" data-mp="3ph94u900n4kg">
                <div data-id="element-4283" data-mp="3uj89dm7hs7iz">
                  <dt className="text-amber-400/55 text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-4284" data-mp="3uiouyfh1w19p">
                    Desire
                  </dt>
                  <dd className="text-white/70 font-light leading-relaxed" data-id="element-4285" data-mp="3kjprrcf20xu3">
                    {c.desire}
                  </dd>
                </div>
                <div data-id="element-4286" data-mp="30ypit0yf6c60">
                  <dt className="text-amber-400/55 text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-4287" data-mp="3100pb8j91y0t">
                    Wound
                  </dt>
                  <dd className="text-white/70 font-light leading-relaxed" data-id="element-4288" data-mp="3awn6nncda6y3">
                    {c.wound}
                  </dd>
                </div>
                <div data-id="element-4289" data-mp="35zd7hfz6egx2">
                  <dt className="text-amber-400/55 text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-4290" data-mp="3jwthf1gokstk">
                    Contradiction
                  </dt>
                  <dd className="text-white/70 font-light leading-relaxed" data-id="element-4291" data-mp="3fuyg3wyr2ffs">
                    {c.contradiction}
                  </dd>
                </div>
                <div className="pt-3 border-t border-amber-400/10" data-id="element-4292" data-mp="3on9x6s7z4qj3">
                  <dt className="text-amber-400/55 text-[9px] tracking-[0.3em] uppercase mb-1 flex items-center gap-1.5" data-id="element-4293" data-mp="3oq4guhj5y7bg">
                    <Music className="w-2.5 h-2.5" data-id="element-4294" data-mp="2gto72yh6lee" />
                    Sonic Motif
                  </dt>
                  <dd className="text-white/55 font-light leading-relaxed italic text-[13px]" data-id="element-4295" data-mp="9ka03p9w1qx7">
                    {c.motif}
                  </dd>
                </div>
              </dl>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// EPISODE ONE
const EpisodeOne = () => {
  const [playing, setPlaying] = useState(false);
  return <section id="episode-one" className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-4296" data-mp="oahcpod7sfpz">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_65%)] pointer-events-none" data-id="element-4297" data-mp="x8kvicwteye3" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-4298" data-mp="1298ov58inrbh">
        <div className="max-w-5xl mx-auto" data-id="element-4299" data-mp="176h5h1e0nnam">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-4300" data-mp="175frao1pekoo">
            <Eyebrow data-id="element-4301" data-mp="2wonsmt87l4rv">Episode One</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-4 leading-tight" data-id="element-4302" data-mp="2wnmeugzt646g">
              A <span className="text-amber-400" data-id="element-4303" data-mp="2rqcg2aqj4g5y">Fractured</span> Punjab
            </h2>
            <div className="flex items-center justify-center gap-4 text-[10px] tracking-[0.3em] uppercase text-white/45 mt-6" data-id="element-4304" data-mp="2mrj2yjf5mr6b">
              <span className="flex items-center gap-1.5" data-id="element-4305" data-mp="3kdgraxg7tqq6">
                <Clock className="w-3 h-3" data-id="element-4306" data-mp="3fevsn2gm3f3u" />
                32 min
              </span>
              <span className="text-amber-400/30" data-id="element-4307" data-mp="3afj48da3dzz5">·</span>
              <span data-id="element-4308" data-mp="3ai5cyg8pyliy">Season One Premiere</span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-4309" data-mp="35gpqqkut28yz">
            <motion.div initial={{
            opacity: 0,
            x: -16
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="lg:col-span-3 relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-8 sm:p-10" data-id="element-4310" data-mp="1z15ipf2t9wma">
              <CornerOrnaments data-id="element-4311" data-mp="38vpmaexy0j8m" />
              <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-4312" data-mp="3dwnc9dp72hko" />
              <div className="flex items-center gap-5 mb-8" data-id="element-4313" data-mp="5ies8u0qmz44">
                <button onClick={() => setPlaying(!playing)} className="group relative w-20 h-20 sm:w-24 sm:h-24 border-2 border-amber-400 bg-amber-400/10 hover:bg-amber-400/20 transition-colors flex items-center justify-center shrink-0 shadow-[0_0_40px_rgba(212,175,55,0.3)]" data-id="element-4314" data-mp="afwq9pd7vtxc">
                  {playing ? <div className="flex gap-1" data-id="element-4315" data-mp="2gb98yp92ymj2">
                      <span className="w-1.5 h-7 bg-amber-400" data-id="element-4316" data-mp="2l8htgwhpb2nh" />
                      <span className="w-1.5 h-7 bg-amber-400" data-id="element-4317" data-mp="2l5vck68x7asl" />
                    </div> : <Play className="w-8 h-8 text-amber-400 fill-amber-400 ml-1" data-id="element-4318" data-mp="ybs1lp1sj4p3" />}
                </button>
                <div className="flex-1 min-w-0" data-id="element-4319" data-mp="1cbkmh3qmyqp5">
                  <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase mb-1.5 font-bold" data-id="element-4320" data-mp="1c8yife2yetbo">
                    {playing ? 'Now Playing' : 'Listen Now'}
                  </p>
                  <p className="font-serif text-xl sm:text-2xl text-white leading-tight" data-id="element-4321" data-mp="kkda0ttthv6f">
                    A Fractured Punjab
                  </p>
                  <p className="text-white/40 text-xs mt-1 font-light" data-id="element-4322" data-mp="tcow63hwewgl">
                    Episode 1 · House of Lions Season One
                  </p>
                </div>
              </div>

              <div className="mb-6" data-id="element-4323" data-mp="vm8clglpeh2">
                <div className="relative h-12 flex items-center gap-0.5" data-id="element-4324" data-mp="ul00oqyz631">
                  {Array.from({
                  length: 60
                }).map((_, i) => <motion.span key={i} animate={playing ? {
                  scaleY: [0.4, 1.2, 0.6, 0.9, 0.4]
                } : {
                  scaleY: 0.4
                }} transition={{
                  duration: 1.5 + i % 4 * 0.2,
                  repeat: Infinity,
                  repeatType: 'mirror',
                  delay: i * 0.02
                }} className={`flex-1 origin-center transition-colors ${i < 12 ? 'bg-amber-400' : 'bg-amber-400/20'}`} style={{
                  height: `${20 + i % 5 * 6}px`
                }} data-id="element-4325" data-mp="1vy990aqtwkwh" data-mpi={i} />)}
                </div>
                <div className="flex items-center justify-between text-[10px] font-mono text-white/40 mt-2" data-id="element-4326" data-mp="33bazgnwgvatr">
                  <span className="text-amber-400" data-id="element-4327" data-mp="ropjaqh9fqib">06:12</span>
                  <span data-id="element-4328" data-mp="mrh1ir00xohl">32:00</span>
                </div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-4329" data-mp="hzfqf8ele3t5">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/50" data-id="element-4330" data-mp="d0mf9mms2ow5">
                  <Volume2 className="w-3 h-3 text-amber-400" data-id="element-4331" data-mp="819v59najvx9" />
                  Best with headphones
                </div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-amber-400/70" data-id="element-4332" data-mp="3yv8sfkeye18">
                  <Bell className="w-3 h-3" data-id="element-4333" data-mp="3v6rbela5nxzx" />
                  Subscribe to follow
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 16
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="lg:col-span-2 relative border border-amber-400/15 bg-gradient-to-b from-amber-950/10 to-transparent p-7" data-id="element-4334" data-mp="1tembamii5ue4">
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" data-id="element-4335" data-mp="njktbsjqd320" />
              <Quote className="w-5 h-5 text-amber-400/40 mb-4" strokeWidth={1.25} data-id="element-4336" data-mp="shw01ueqaxg0" />
              <p className="text-amber-400 text-[9px] tracking-[0.3em] uppercase font-bold mb-4" data-id="element-4337" data-mp="jir5ez6afo94">
                Cold Open
              </p>
              <p className="font-serif text-lg text-white/80 leading-relaxed mb-5" data-id="element-4338" data-mp="4n1qfkzisbdw">
                &ldquo;Punjab did not begin as one song. It began as many fires,
                burning close enough to warm the same dark, far enough apart to
                mistake each other for threat.&rdquo;
              </p>
              <p className="text-white/45 text-sm font-light italic leading-relaxed" data-id="element-4339" data-mp="23y2w4izqu565">
                In a fractured land, history is not first felt in crowns. It is
                felt in doors bolted before dusk.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6" data-id="element-4340" data-mp="22dgvq2csucr">
            {[{
            n: '01',
            title: 'Chorus of Fracture',
            body: 'A montage of voices — soldier, merchant, elder, rider — that refuses to resolve.'
          }, {
            n: '04',
            title: 'The Logic of the Misls',
            body: '"Boys always think a sword is an answer. Men live long enough to learn it is a receipt."'
          }, {
            n: '08',
            title: 'Lahore as Desire',
            body: 'Cities do not love ambition. They ask: are you here to possess me, or to be remade?'
          }].map(s => <div key={s.n} className="border border-amber-400/15 bg-black/40 p-4" data-id="element-4341" data-mp="1qqne6b9ons84">
                <p className="font-mono text-amber-400/55 text-[10px] tracking-[0.2em] mb-1.5" data-id="element-4342" data-mp="1mothee2lz8d8">
                  SCENE {s.n}
                </p>
                <h4 className="font-serif text-white text-base leading-tight mb-2" data-id="element-4343" data-mp="1hw8oderj0zar">
                  {s.title}
                </h4>
                <p className="text-white/50 text-xs font-light leading-relaxed italic" data-id="element-4344" data-mp="17zx8kesq9rbk">
                  {s.body}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
// SONIC IDENTITY
const SonicIdentity = () => {
  const zones = [{
    icon: Crown,
    name: 'Lahore Court',
    desc: 'Layered air, distant cloth, softened marble reverb, subtle metal ornament, birds and fountain at the edge.'
  }, {
    icon: Mountain,
    name: 'The Frontier',
    desc: 'Dry wind, saddle leather, hoof rhythm, iron, wider silence, colder space, distant threat.'
  }, {
    icon: Heart,
    name: 'Private Chamber',
    desc: 'Lower room tone, breath, jewelry contact, close fabric, intimacy with danger.'
  }, {
    icon: Flame,
    name: 'Faith & Judgment',
    desc: 'Reduced instrumentation, room pressure, tonal narrowing, voice against stillness.'
  }, {
    icon: Scroll,
    name: 'Memory & Inheritance',
    desc: 'Degraded textures, echo fragments, recurring motifs in altered form.'
  }];
  return <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-4345" data-mp="2hz852i1eprah">
      <div className="container mx-auto px-4" data-id="element-4346" data-mp="2mw6yeyd8xu7m">
        <SectionHeading eyebrow="The Sonic World" title="Sound as" highlight="World-Building Language" intro="Each place, person, and moral force carries a recognizable sonic signature. The world is built not through narration, but through breath, room tone, and rhythm." data-id="element-4347" data-mp="2mzclk82bem0p" />
        <div className="mt-14 max-w-5xl mx-auto space-y-3" data-id="element-4348" data-mp="23909se3pmrkg">
          {zones.map((z, i) => <motion.div key={z.name} initial={{
          opacity: 0,
          x: -16
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="relative flex items-start gap-5 border border-amber-400/15 hover:border-amber-400/35 transition-colors bg-gradient-to-r from-amber-950/10 to-transparent p-6" data-id="element-4349" data-mp="1ya5rdd9nn50v" data-mpi={i}>
              <div className="w-11 h-11 border border-amber-400/40 bg-black/40 flex items-center justify-center shrink-0" data-id="element-4350" data-mp="1ojuh78zdnzlj">
                <z.icon className="w-5 h-5 text-amber-400" strokeWidth={1.25} data-id="element-4351" data-mp="10tgttvrqmoxa" />
              </div>
              <div className="flex-1" data-id="element-4352" data-mp="vux9fzssll71">
                <h4 className="font-serif text-xl text-white leading-tight mb-1.5" data-id="element-4353" data-mp="vtug7l7zz6ny">
                  {z.name}
                </h4>
                <p className="text-white/55 text-sm sm:text-[15px] font-light leading-relaxed" data-id="element-4354" data-mp="3caa9224e65oz">
                  {z.desc}
                </p>
              </div>
              <div className="hidden sm:flex items-end gap-0.5 h-8 shrink-0" data-id="element-4355" data-mp="2ygj0ox2ecruu">
                {Array.from({
              length: 6
            }).map((_, j) => <span key={j} className="w-0.5 bg-amber-400/40" style={{
              height: `${10 + (i * 3 + j) % 5 * 4}px`
            }} data-id="element-4356" data-mp="3r7j6ndkag5p5" data-mpi={j} />)}
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// LISTENING ARCHITECTURE
const ListeningArchitecture = () => {
  const formats = [{
    icon: Headphones,
    tag: 'Flagship',
    title: 'House of Lions',
    sub: 'Season One: The Making of Lahore',
    desc: 'Eight prestige dramatic episodes. The narrative core of the franchise.',
    access: 'Public · All platforms'
  }, {
    icon: Mic,
    tag: 'Short-Form',
    title: 'Voices of the Court',
    sub: 'Portrait Monologues',
    desc: 'Single-character intimate monologues — direct voice, no narration.',
    access: 'Public · Discovery'
  }, {
    icon: Radio,
    tag: 'Ambient',
    title: 'Echoes of Lahore',
    sub: 'Place & Memory Worlds',
    desc: 'Ambient sound worlds for slow listening. Court, frontier, chamber, courtyard.',
    access: 'Public · Atmospheric'
  }, {
    icon: Lock,
    tag: 'Premium',
    title: 'Sealed Chambers',
    sub: 'Treasury & Patron Drops',
    desc: 'Extended scenes, alternate perspectives, unreleased monologues. Unlocked by XP, Misls, and Court status.',
    access: 'Premium · Treasury-linked',
    featured: true
  }, {
    icon: Users,
    tag: 'Live',
    title: 'Darbar Sessions',
    sub: 'Salons & Event Audio',
    desc: 'Live recordings from in-person events. City-specific drops, conversations with the creators.',
    access: 'Event & live spillover'
  }];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-4357" data-mp="1jf0maj4omj7f">
      <div className="container mx-auto px-4" data-id="element-4358" data-mp="1o83ikz0ug6rb">
        <SectionHeading eyebrow="The Listening Architecture" title="Five Ways to" highlight="Enter the World" intro="A prestige narrative core surrounded by social-first character clips, ambient worlds, premium collectible-linked drops, and live event spillover." data-id="element-4359" data-mp="1s8x63radjjjo" />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-id="element-4360" data-mp="275w88zmgik6r">
          {formats.map((f, i) => <motion.div key={f.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className={`relative border bg-gradient-to-b from-amber-950/10 to-black p-6 transition-colors ${f.featured ? 'border-amber-400/45 hover:border-amber-400' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-4361" data-mp="7ud9teys4ubc" data-mpi={i}>
              <span className={`absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent ${f.featured ? 'via-amber-400' : 'via-amber-400/55'} to-transparent`} data-id="element-4362" data-mp="irhx3ezxy5vq" />
              <div className="flex items-center justify-between mb-4" data-id="element-4363" data-mp="sghzz9hkdlf7">
                <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center" data-id="element-4364" data-mp="sjdzh2928i5r">
                  <f.icon className="w-4 h-4 text-amber-400" data-id="element-4365" data-mp="12fha6whz9p4b" />
                </div>
                <span className={`text-[9px] tracking-[0.3em] uppercase font-bold px-2 py-1 border ${f.featured ? 'border-amber-400 text-amber-400 bg-amber-400/10' : 'border-amber-400/30 text-amber-400/80'}`} data-id="element-4366" data-mp="12deb7myuklng">
                  {f.tag}
                </span>
              </div>
              <h4 className="font-serif text-xl text-white leading-tight mb-1" data-id="element-4367" data-mp="1h1o3p9zrm9pl">
                {f.title}
              </h4>
              <p className="text-amber-400/70 text-[11px] tracking-[0.2em] uppercase mb-3 font-light" data-id="element-4368" data-mp="12crv9btsquog">
                {f.sub}
              </p>
              <p className="text-white/55 text-sm font-light leading-relaxed mb-4" data-id="element-4369" data-mp="175tf7y8v1kew">
                {f.desc}
              </p>
              <p className="text-white/35 text-[10px] tracking-[0.25em] uppercase border-t border-amber-400/10 pt-3 flex items-center gap-1.5" data-id="element-4370" data-mp="xdnqcxdwnj3x">
                <span className="text-amber-400/60" data-id="element-4371" data-mp="jfgybxvn0dc6">•</span> {f.access}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// CLOSING CTA
const ClosingCTA = () => {
  const platforms = ['Apple Podcasts', 'Spotify', 'YouTube', 'Overcast', 'Pocket Casts'];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-4372" data-mp="yucmkkgjplg1">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.1),_transparent_60%)] pointer-events-none" data-id="element-4373" data-mp="uwet35fqsd6u" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(139,0,0,0.08),_transparent_60%)] pointer-events-none" data-id="element-4374" data-mp="kxfxang2sc19" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-4375" data-mp="g3l48tekykpi">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto text-center" data-id="element-4376" data-mp="b7mmhonrc0y2">
          <Eyebrow data-id="element-4377" data-mp="3td91c2t7zv1f">Begin Listening</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-4378" data-mp="3teamx17v3zxe">
            The court is{' '}
            <span className="text-amber-400 italic" data-id="element-4379" data-mp="2ds748lvzut69">still breathing.</span>
          </h2>
          <div className="w-20 h-px bg-amber-400/40 mx-auto mb-8" data-id="element-4380" data-mp="2is37giwvagkr" />
          <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed mb-10" data-id="element-4381" data-mp="2mty7lk32anwy">
            Subscribe to House of Lions on your platform of choice. Or step
            deeper into the world through Lahore Treasury, where every episode
            unlocks new chambers.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10" data-id="element-4382" data-mp="1v0edky4edk1p">
            <a href="#episode-one" className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-4383" data-mp="1z2izfaoltcds">
              <Play className="w-4 h-4 fill-black" data-id="element-4384" data-mp="1bcexr8uz7mpf" />
              Listen to Episode One
            </a>
            <a href="/treasure" className="group inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-4385" data-mp="3n0a9r8g9weip">
              <Sparkles className="w-3.5 h-3.5" data-id="element-4386" data-mp="9n74lg6dmvev" />
              Enter Lahore Treasury
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-4387" data-mp="9pb8vz86twfo" />
            </a>
          </div>
          <div className="border-t border-amber-400/10 pt-8" data-id="element-4388" data-mp="inddspv1l988">
            <p className="text-amber-400/55 text-[10px] tracking-[0.3em] uppercase mb-5 font-light" data-id="element-4389" data-mp="ilshaup63g7q">
              Available On
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-id="element-4390" data-mp="2kfrwhr5euhpp">
              {platforms.map(p => <span key={p} className="px-3 py-1.5 border border-amber-400/20 text-white/55 text-[11px] tracking-wide hover:border-amber-400/50 hover:text-white/80 transition-colors cursor-pointer" data-id="element-4391" data-mp="2pf4i68kjpiqp">
                  {p}
                </span>)}
            </div>
            <p className="mt-6 text-white/35 text-xs font-light italic flex items-center justify-center gap-2" data-id="element-4392" data-mp="282o1uq1nhvzg">
              <ChevronRight className="w-3 h-3 text-amber-400/50" data-id="element-4393" data-mp="30to6923w91rn" />
              Episodes release weekly when the season opens.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
};
export const HouseOfLionsPage = () => <LanguageProvider data-id="element-4394" data-mp="2qu72wrc46zyu">
    <div className="bg-black min-h-screen text-white selection:bg-amber-400/30" data-id="element-4395" data-mp="2qtp1zheibxkl">
      <ScrollProgress data-id="element-4396" data-mp="2mtdgk2eblt82" />
      <Navbar data-id="element-4397" data-mp="2mvzm5whnhynn" />
      <main data-id="element-4398" data-mp="1fgd2tjtp034r">
        <Hero data-id="element-4399" data-mp="1fg3es1s7k61p" />
        <CentralQuestion data-id="element-4400" data-mp="1fglfpbptf8fy" />
        <TheWorld data-id="element-4401" data-mp="1fhwlfh2tmq9p" />
        <SeasonOne data-id="element-4402" data-mp="1fe8oe7y63lq8" />
        <Ensemble data-id="element-4403" data-mp="1berkvk2awynp" />
        <EpisodeOne data-id="element-4404" data-mp="1be8birqpvila" />
        <SonicIdentity data-id="element-4405" data-mp="1bdowpjwd5abh" />
        <ListeningArchitecture data-id="element-4406" data-mp="1bdggpj4y1ow2" />
        <ClosingCTA data-id="element-4407" data-mp="1cvx65gsnfret" />
      </main>
      <Footer data-id="element-4408" data-mp="1cwym9zoiv43e" />
    </div>
  </LanguageProvider>;